import React, { useState, useRef, useEffect } from 'react';
import { usersApi, configApi, clientsApi } from '../services/api';
import { User } from '../contexts/AuthContext';

interface Sucursal {
  nombre: string;
  direccion: string;
  dvr_id: string;
  dvr_password: string;
  precio_normal: number;
  precio_urgente: number;
  precio_prioritaria: number;
  // Configuración CRÍTICA de tiempo (control operador)
  tiempo_auditoria: number; // minutos que dura la auditoría (para control operador)
  // Nuevo: tiempo específico para esta sucursal (opcional, hereda del cliente si está vacía)
  tiempoEstimadoHoras?: number; // horas específicas para esta sucursal
  // Sistema 4 auditorías fijas
  auditoria_fija_mes: boolean; // Si se cobra 4 auditorías fijas al mes
  // Nuevos campos de pago operador específicos por sucursal (opcionales, heredan del cliente si están vacíos)
  comisionOperador?: number; // % si tipoComision = 'porcentaje'
  montoFijoOperador?: number; // $ si tipoComision = 'fijo'
  tipoComision?: string; // 'porcentaje' | 'fijo'
  modalidad_auditoria: string;
  dias_auditoria: string[];
  fecha_exacta?: string;
  urgencia: boolean;
  prioritaria: boolean;
  activa: boolean;
  instrucciones_op: string;
}

interface ClientFormData {
  // Datos básicos
  nombre: string;
  email: string;
  cuit: string;
  telefono: string;
  direccion: string;
  sitioWeb: string;
  contactoPrincipal: string;
  facturacion: string;
  activo: boolean;
  condicionesPago: string;
  
  // Configuración auditorías CRÍTICA
  costoAuditoria: number;
  tiempoEstimado: string;
  comisionOperador: number;
  montoFijoOperador: number;
  tipoComision: string;
  limiteDias: number;
  
  // Sistema 4 auditorías fijas + extras (LÓGICA CLAVE DE NEGOCIO)
  auditoria_fija_mes: boolean; // Si marca tilde, cobra 4 fijas + extras
  
  // Tipo auditoría por defecto
  tipo_auditoria_default: string; // normal, prioritaria, urgente
  
  // Configuración auditoría directa (cuando tiene 1 sucursal)
  tiempo_auditoria_cliente: number; // minutos
  dvr_id_principal: string;
  dvr_password_principal: string;
  instrucciones_operador: string;
  
  // Contrato (OBLIGATORIO)
  contrato_adjunto: any; // Archivo del contrato
  contrato_fecha_firma: string; // Fecha de firma del contrato
  contrato_observaciones: string; // Observaciones del contrato
  
  // Estado de cuenta
  estadoCuenta: string;
  saldoPendiente: number;
  descuentoVolumen: number;
  
  // Usuario asociado
  crearUsuario: boolean;
  username: string;
  password: string;
  confirmarPassword: string;
  rol: string;
  mostrarPassword: boolean;
  
  // Sucursales
  sucursales: Sucursal[];
}

interface ClientFormCompleteProps {
  user: User;
  clientToEdit?: any; // Cliente para editar (opcional)
  isEditing?: boolean; // Si está en modo edición
  onSuccess: () => void;
  onCancel: () => void;
}

const DIAS = ["Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado", "Domingo"];
const ROLES_USUARIO = ["cliente", "admin", "operador", "auditor"];

const ClientFormComplete: React.FC<ClientFormCompleteProps> = ({ user, clientToEdit, isEditing, onSuccess, onCancel }) => {
  const [cliente, setCliente] = useState<ClientFormData>({
    // Datos básicos
    nombre: '',
    email: '',
    cuit: '',
    telefono: '',
    direccion: '',
    sitioWeb: '',
    contactoPrincipal: '',
    facturacion: '',
    activo: true,
    condicionesPago: 'contado',
    
    // Configuración auditorías CRÍTICA
    costoAuditoria: 100,
    tiempoEstimado: '04:00',
    comisionOperador: 0,
    montoFijoOperador: 100,
    tipoComision: 'fijo',
    limiteDias: 30,
    
    // Sistema 4 auditorías fijas + extras
    auditoria_fija_mes: false, // Por defecto false
    
    // Tipo auditoría por defecto
    tipo_auditoria_default: 'normal', // Por defecto normal
    
    // Configuración auditoría directa (1 sucursal)
    tiempo_auditoria_cliente: 120, // 2 horas por defecto
    dvr_id_principal: '',
    dvr_password_principal: '',
    instrucciones_operador: '',
    
    // Contrato (OBLIGATORIO)
    contrato_adjunto: null,
    contrato_fecha_firma: '',
    contrato_observaciones: '',
    
    // Estado de cuenta
    estadoCuenta: 'al dia',
    saldoPendiente: 0,
    descuentoVolumen: 0,
    
    // Usuario asociado
    crearUsuario: true,
    username: 'demo',
    password: 'demo123456',
    confirmarPassword: 'demo123456',
    rol: 'cliente',
    mostrarPassword: false,
    
    // Sucursales
    sucursales: []
  });

  const [sucursal, setSucursal] = useState<Sucursal | null>(null);
  const [editingSuc, setEditingSuc] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [clientesExistentes, setClientesExistentes] = useState<any[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const contratoInputRef = useRef<HTMLInputElement>(null);

  const passwordsCoinciden = cliente.password === cliente.confirmarPassword;
  const contratoValido = cliente.contrato_adjunto && (cliente.contrato_adjunto.nombre || cliente.contrato_adjunto.name);

  // Cargar datos del cliente cuando se está editando
  useEffect(() => {
    if (isEditing && clientToEdit) {
      setCliente(prev => ({
        ...prev,
        // Datos básicos del cliente
        nombre: clientToEdit.nombre || '',
        email: clientToEdit.email || '',
        cuit: clientToEdit.cuit || clientToEdit.rut || '',
        telefono: clientToEdit.telefono || '',
        direccion: clientToEdit.direccion || '',
        sitioWeb: clientToEdit.sitioWeb || '',
        contactoPrincipal: clientToEdit.contactoPrincipal || '',
        facturacion: clientToEdit.facturacion || '',
        activo: clientToEdit.activo !== undefined ? clientToEdit.activo : true,
        condicionesPago: clientToEdit.condicionesPago || 'contado',
        
        // Configuración auditorías
        costoAuditoria: clientToEdit.costoAuditoria || 100,
        tiempoEstimado: clientToEdit.tiempoEstimado ? clientToEdit.tiempoEstimado : '04:00',
        comisionOperador: clientToEdit.comisionOperador || 0,
        montoFijoOperador: clientToEdit.montoFijoOperador || 100,
        tipoComision: clientToEdit.tipoComision || 'fijo',
        limiteDias: clientToEdit.limiteDias || 30,
        
        // Sistema 4 auditorías fijas
        auditoria_fija_mes: clientToEdit.auditoria_fija_mes || false,
        
        // Tipo auditoría por defecto
        tipo_auditoria_default: clientToEdit.tipo_auditoria_default || 'normal',

        // Configuración auditoría directa (DVR y tiempo)
        tiempo_auditoria_cliente: clientToEdit.tiempo_auditoria_cliente || 120,
        dvr_id_principal: clientToEdit.dvr_id_principal || '',
        dvr_password_principal: clientToEdit.dvr_password_principal || '',
        instrucciones_operador: clientToEdit.instrucciones_operador || '',

        // Contrato
        contrato_adjunto: clientToEdit.contrato_adjunto || null,
        contrato_fecha_firma: clientToEdit.contrato_fecha_firma || '',
        contrato_observaciones: clientToEdit.contrato_observaciones || '',
        
        // Estado de cuenta
        estadoCuenta: clientToEdit.estadoCuenta || 'al dia',
        saldoPendiente: clientToEdit.saldoPendiente || 0,
        descuentoVolumen: clientToEdit.descuentoVolumen || 0,
        
        // Sucursales (cargar las existentes y asegurar campos requeridos)
        sucursales: (clientToEdit.sucursales || []).map(suc => ({
          ...suc,
          tiempo_auditoria: suc.tiempo_auditoria || 120,
          dvr_id: suc.dvr_id || '',
          dvr_password: suc.dvr_password || '',
          instrucciones_op: suc.instrucciones_op || ''
        })),
        // No cargar datos de usuario para edición (por seguridad)
        crearUsuario: false,
        username: '',
        password: '',
        confirmarPassword: ''
      }));
    }
  }, [isEditing, clientToEdit]);

  useEffect(() => {
    loadClientes();
  }, []);

  const loadClientes = async () => {
    try {
      const response = await clientsApi.getAll();
      if (response.data.success) {
        setClientesExistentes(response.data.data);
      }
    } catch (error) {
      console.error('Error cargando clientes:', error);
    }
  };

  const handleEditClient = (cliente: any) => {
    // Llenar el formulario con los datos del cliente existente
    setCliente({
      ...clienteInicial,
      ...cliente,
      // Asegurar que sucursales sea un array
      sucursales: cliente.sucursales || []
    });
    setIsEditing(true);
    setEditingClientId(cliente.id);
    setError('');
    setSuccess('');
  };

  const generarPassword = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let password = '';
    for (let i = 0; i < 8; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setCliente(c => ({ ...c, password, confirmarPassword: password }));
  };

  const handleSucGuardar = () => {
    if (!sucursal) return;

    let nueva = { ...sucursal };
    const arr = cliente.sucursales.slice();
    if (editingSuc !== null) arr[editingSuc] = nueva;
    else arr.push(nueva);
    setCliente(c => ({ ...c, sucursales: arr }));
    setSucursal(null);
    setEditingSuc(null);
  };

  const eliminarSucursal = (i: number) => {
    setCliente(c => ({ ...c, sucursales: c.sucursales.filter((_, idx) => idx !== i) }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      // Validaciones OBLIGATORIAS
      if (!cliente.nombre || !cliente.email) {
        throw new Error('Nombre y email son obligatorios');
      }

      // VALIDACIÓN CONTRATO OBLIGATORIO
      if (!contratoValido) {
        throw new Error('El contrato es obligatorio. Debe adjuntar el archivo firmado.');
      }

      if (cliente.crearUsuario) {
        if (!cliente.username || !cliente.password) {
          throw new Error('Username y password son obligatorios para crear usuario');
        }
        if (!passwordsCoinciden) {
          throw new Error('Las contraseñas no coinciden');
        }
      }

      let response;

      if (isEditing && clientToEdit) {
        // ACTUALIZAR cliente existente
        const updateData = {
          nombre: cliente.nombre,
          email: cliente.email,
          cuit: cliente.cuit,
          telefono: cliente.telefono,
          direccion: cliente.direccion,
          contactoPrincipal: cliente.contactoPrincipal,
          activo: cliente.activo,
          // Datos adicionales que pueden haberse configurado
          sucursales: cliente.sucursales,
          costoAuditoria: cliente.costoAuditoria,
          tiempoEstimado: cliente.tiempoEstimado,
          comisionOperador: cliente.comisionOperador,
          montoFijoOperador: cliente.montoFijoOperador,
          tipoComision: cliente.tipoComision,
          // Contrato
          contrato_adjunto: cliente.contrato_adjunto,
          contrato_fecha_firma: cliente.contrato_fecha_firma,
          contrato_observaciones: cliente.contrato_observaciones,
          // Sistema 4 auditorías
          auditoria_fija_mes: cliente.auditoria_fija_mes,
          // Tipo auditoría por defecto
          tipo_auditoria_default: cliente.tipo_auditoria_default,
          // Configuración DVR y tiempo de auditoría
          tiempo_auditoria_cliente: cliente.tiempo_auditoria_cliente,
          dvr_id_principal: cliente.dvr_id_principal,
          dvr_password_principal: cliente.dvr_password_principal,
          instrucciones_operador: cliente.instrucciones_operador
        };

        response = await clientsApi.update(clientToEdit.id, updateData);
        
        if (response.data.success) {
          setSuccess('Cliente actualizado exitosamente');
          setTimeout(() => {
            onSuccess();
          }, 1500);
        }
      } else {
        // CREAR nuevo cliente y usuario
        const userData = {
          email: cliente.email,
          username: cliente.crearUsuario ? cliente.username : null,
          password: cliente.crearUsuario ? cliente.password : 'temp123456',
          name: cliente.nombre,
          role: cliente.crearUsuario ? cliente.rol : 'cliente',
          // Datos completos del cliente en un campo especial
          clientData: {
            ...cliente,
            password: undefined,
            confirmarPassword: undefined,
            mostrarPassword: undefined
          }
        };

        response = await clientsApi.create(userData);

        if (response.data.success) {
          setSuccess('Cliente y usuario creados exitosamente');
          setTimeout(() => {
            onSuccess();
          }, 1500);
        }
      }
    } catch (error: any) {
      console.error('Error creando cliente:', error);
      setError(error.response?.data?.error || error.message || 'Error creando cliente');
    } finally {
      setLoading(false);
    }
  };

  const formStyles = {
    container: {
      maxWidth: '900px',
      margin: '0 auto',
      backgroundColor: 'white',
      borderRadius: '12px',
      padding: '2rem',
      color: '#333',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
    },
    section: {
      marginBottom: '2rem',
      padding: '1.5rem',
      backgroundColor: '#f8f9fa',
      borderRadius: '8px',
      border: '1px solid #e0e0e0'
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
      gap: '1rem',
      marginBottom: '1rem'
    },
    grid3: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '1rem',
      marginBottom: '1rem'
    },
    label: {
      display: 'block',
      marginBottom: '0.5rem',
      fontWeight: '500',
      color: '#555'
    },
    input: {
      width: '100%',
      padding: '0.75rem',
      backgroundColor: 'white',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      color: '#333',
      fontSize: '0.875rem'
    }
  };

  return (
    <>
    <div style={formStyles.container}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', margin: 0, color: '#4CAF50' }}>
          {isEditing ? '✏️ Editar Cliente' : '🏢 Crear Nuevo Cliente Completo'}
        </h2>
        {isEditing && (
          <button
            type="button"
            onClick={() => {
              setCliente(clienteInicial);
              setIsEditing(false);
              setEditingClientId(null);
              setError('');
              setSuccess('');
            }}
            className="btn btn-secondary"
          >
            ➕ Nuevo Cliente
          </button>
        )}
      </div>

      {error && (
        <div style={{ 
          backgroundColor: '#f443364d', 
          color: '#ff6b6b', 
          padding: '1rem', 
          borderRadius: '6px', 
          marginBottom: '1rem',
          border: '1px solid #f44336'
        }}>
          {error}
        </div>
      )}

      {success && (
        <div style={{ 
          backgroundColor: '#4caf504d', 
          color: '#81c784', 
          padding: '1rem', 
          borderRadius: '6px', 
          marginBottom: '1rem',
          border: '1px solid #4caf50'
        }}>
          {success}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* Información Básica */}
        <div style={formStyles.section}>
          <h4 style={{ color: "#4CAF50", marginBottom: '1rem' }}>📋 Información Básica</h4>
          <div style={formStyles.grid}>
            <label style={formStyles.label}>
              Nombre *:
              <input
                style={formStyles.input}
                value={cliente.nombre}
                onChange={e => setCliente(c => ({ ...c, nombre: e.target.value }))}
                required
                placeholder="Nombre de la empresa"
              />
            </label>
            <label style={formStyles.label}>
              CUIT:
              <input
                style={formStyles.input}
                value={cliente.cuit}
                onChange={e => setCliente(c => ({ ...c, cuit: e.target.value }))}
                placeholder="20-12345678-9"
              />
            </label>
            <label style={formStyles.label}>
              Email *:
              <input
                type="email"
                style={formStyles.input}
                value={cliente.email}
                onChange={e => setCliente(c => ({ ...c, email: e.target.value }))}
                required
                placeholder="email@empresa.com"
              />
            </label>
            <label style={formStyles.label}>
              Teléfono:
              <input
                style={formStyles.input}
                value={cliente.telefono}
                onChange={e => setCliente(c => ({ ...c, telefono: e.target.value }))}
                placeholder="+54 11 1234-5678"
              />
            </label>
          </div>
          
          <div style={formStyles.grid}>
            <label style={formStyles.label}>
              Dirección:
              <input
                style={formStyles.input}
                value={cliente.direccion}
                onChange={e => setCliente(c => ({ ...c, direccion: e.target.value }))}
                placeholder="Dirección principal"
              />
            </label>
            <label style={formStyles.label}>
              Contacto Principal:
              <input
                style={formStyles.input}
                value={cliente.contactoPrincipal}
                onChange={e => setCliente(c => ({ ...c, contactoPrincipal: e.target.value }))}
                placeholder="Nombre del contacto principal"
              />
            </label>
          </div>

          <label style={formStyles.label}>
            Facturación:
            <input
              style={formStyles.input}
              value={cliente.facturacion}
              onChange={e => setCliente(c => ({ ...c, facturacion: e.target.value }))}
              placeholder="Datos para facturación"
            />
          </label>
          
          <div style={{ display: "flex", alignItems: "center", gap: '2rem', marginTop: '1rem' }}>
            <label style={{ ...formStyles.label, display: "flex", alignItems: "center" }}>
              <input
                type="checkbox"
                checked={cliente.activo}
                onChange={e => setCliente(c => ({ ...c, activo: e.target.checked }))}
                style={{ marginRight: '0.5rem' }}
              />
              Cliente Activo
            </label>
            
            <label style={formStyles.label}>
              Condiciones de Pago:
              <select
                style={formStyles.input}
                value={cliente.condicionesPago}
                onChange={e => setCliente(c => ({ ...c, condicionesPago: e.target.value }))}
              >
                <option value="contado">Contado</option>
                <option value="15 días">15 días</option>
                <option value="30 días">30 días</option>
                <option value="60 días">60 días</option>
                <option value="90 días">90 días</option>
              </select>
            </label>
          </div>
        </div>

        {/* SOLO MOSTRAR CONFIGURACIÓN COMPLETA CUANDO NO HAY SUCURSALES */}
        {cliente.sucursales.length === 0 && (
          <>
            {/* 2. INFORMACIÓN DVR */}
            <div style={formStyles.section}>
              <h4 style={{ color: "#4CAF50", marginBottom: '1rem' }}>🖥️ Información DVR/DVRs</h4>
              <div style={formStyles.grid}>
                <label style={formStyles.label}>
                  DVR ID/IP *:
                  <input
                    style={formStyles.input}
                    value={cliente.dvr_id_principal}
                    onChange={e => setCliente(c => ({ ...c, dvr_id_principal: e.target.value }))}
                    placeholder="192.168.1.100 o ID del DVR"
                    required
                  />
                </label>
                <label style={formStyles.label}>
                  DVR Password:
                  <input
                    type="text"
                    style={formStyles.input}
                    value={cliente.dvr_password_principal}
                    onChange={e => setCliente(c => ({ ...c, dvr_password_principal: e.target.value }))}
                    placeholder="Contraseña del DVR (opcional)"
                  />
                </label>
              </div>
            </div>

            {/* 4. VALOR DE LA AUDITORÍA */}
            <div style={formStyles.section}>
              <h4 style={{ color: "#4CAF50", marginBottom: '1rem' }}>💰 Valor de la Auditoría</h4>
              <div style={formStyles.grid}>
                <label style={formStyles.label}>
                  Costo por Auditoría ($) *:
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    style={formStyles.input}
                    value={cliente.costoAuditoria}
                    onChange={e => setCliente(c => ({ ...c, costoAuditoria: parseFloat(e.target.value) || 0 }))}
                    required
                  />
                </label>
              </div>
            </div>

            {/* 5. VALOR PAGO AL OPERADOR */}
            <div style={formStyles.section}>
              <h4 style={{ color: "#4CAF50", marginBottom: '1rem' }}>👷 Valor Pago al Operador</h4>
              <div style={formStyles.grid}>
                <label style={formStyles.label}>
                  Tipo de Comisión *:
                  <select
                    style={formStyles.input}
                    value={cliente.tipoComision}
                    onChange={e => setCliente(c => ({ ...c, tipoComision: e.target.value }))}
                    required
                  >
                    <option value="porcentaje">Porcentaje (%)</option>
                    <option value="fijo">Monto Fijo ($)</option>
                  </select>
                </label>
                
                {cliente.tipoComision === 'porcentaje' && (
                  <label style={formStyles.label}>
                    Comisión Operador (%) *:
                    <input
                      type="number"
                      min="0"
                      max="100"
                      style={formStyles.input}
                      value={cliente.comisionOperador}
                      onChange={e => setCliente(c => ({ ...c, comisionOperador: parseInt(e.target.value) || 0 }))}
                      required
                    />
                  </label>
                )}

                {cliente.tipoComision === 'fijo' && (
                  <label style={formStyles.label}>
                    Monto Fijo por Auditoría ($) *:
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      style={formStyles.input}
                      value={cliente.montoFijoOperador}
                      onChange={e => setCliente(c => ({ ...c, montoFijoOperador: parseFloat(e.target.value) || 0 }))}
                      required
                    />
                  </label>
                )}
              </div>
              
              <div style={{ marginTop: '1rem', padding: '1rem', backgroundColor: '#f0f8ff', borderRadius: '6px', border: '1px solid #2196f3' }}>
                <strong>Ejemplo:</strong>{' '}
                {cliente.tipoComision === 'porcentaje' 
                  ? `Con auditoría de $${cliente.costoAuditoria}, el operador recibe $${((cliente.costoAuditoria * cliente.comisionOperador) / 100).toFixed(2)} (${cliente.comisionOperador}%)`
                  : `El operador recibe siempre $${cliente.montoFijoOperador} por auditoría`
                }
              </div>
            </div>

            {/* 6. HORA DE CONTROL */}
            <div style={formStyles.section}>
              <h4 style={{ color: "#4CAF50", marginBottom: '1rem' }}>⏰ Hora de Control</h4>
              <div style={formStyles.grid}>
                <label style={formStyles.label}>
                  Tiempo Estimado (hh:mm) *:
                  <input
                    type="time"
                    style={formStyles.input}
                    value={cliente.tiempoEstimado}
                    onChange={e => setCliente(c => ({ ...c, tiempoEstimado: e.target.value }))}
                    required
                  />
                </label>
              </div>
              <p style={{ fontSize: '0.875rem', color: '#666', margin: '0.5rem 0 0 0' }}>
                Este tiempo será usado para controlar que las auditorías no se extiendan demasiado.
              </p>
            </div>

            {/* 7. AUDITORÍAS CON PRIORIDADES */}
            <div style={formStyles.section}>
              <h4 style={{ color: "#4CAF50", marginBottom: '1rem' }}>🎯 Auditorías con Prioridades</h4>
              <div style={{ padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '6px', border: '1px solid #e0e0e0' }}>
                <div style={{ marginBottom: '1rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <div style={{ 
                      width: '20px', 
                      height: '20px', 
                      backgroundColor: '#4CAF50', 
                      borderRadius: '50%', 
                      marginRight: '0.75rem' 
                    }}></div>
                    <strong>Normal: ${cliente.costoAuditoria}</strong> (Precio estándar)
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <div style={{ 
                      width: '20px', 
                      height: '20px', 
                      backgroundColor: '#FF9800', 
                      borderRadius: '50%', 
                      marginRight: '0.75rem' 
                    }}></div>
                    <strong>Prioritaria: ${(cliente.costoAuditoria * 1.5).toFixed(2)}</strong> (+50% = x1.5)
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div style={{ 
                      width: '20px', 
                      height: '20px', 
                      backgroundColor: '#f44336', 
                      borderRadius: '50%', 
                      marginRight: '0.75rem' 
                    }}></div>
                    <strong>Urgente: ${(cliente.costoAuditoria * 2).toFixed(2)}</strong> (x2 = doble precio)
                  </div>
                </div>
                <p style={{ fontSize: '0.875rem', color: '#666', margin: 0 }}>
                  El cliente puede solicitar auditorías normales, prioritarias o urgentes. Los precios se calculan automáticamente.
                </p>
              </div>
            </div>

            {/* 10. MODALIDAD, INSTRUCCIONES, ARCHIVO PROCEDIMIENTOS */}
            <div style={formStyles.section}>
              <h4 style={{ color: "#4CAF50", marginBottom: '1rem' }}>📋 Modalidad e Instrucciones</h4>
              
              <label style={formStyles.label}>
                Modalidad de Auditoría *:
                <select
                  style={formStyles.input}
                  value={cliente.tipo_auditoria_default}
                  onChange={e => setCliente(c => ({ ...c, tipo_auditoria_default: e.target.value }))}
                  required
                >
                  <option value="normal">🟢 Normal (Precio estándar)</option>
                  <option value="prioritaria">🟡 Prioritaria (+50%)</option>
                  <option value="urgente">🔴 Urgente (Doble precio)</option>
                </select>
              </label>

              <label style={formStyles.label}>
                Modalidad de Auditoría:
                <select
                  style={formStyles.input}
                  value={cliente.modalidad_auditoria_cliente || 'manual/hoy'}
                  onChange={e => setCliente(c => ({ ...c, modalidad_auditoria_cliente: e.target.value }))}
                >
                  <option value="manual/hoy">Manual/Hoy</option>
                  <option value="por días">Por días de la semana</option>
                  <option value="por fecha exacta">Por fecha exacta</option>
                </select>
              </label>

              {cliente.modalidad_auditoria_cliente === "por días" && (
                <label style={formStyles.label}>
                  Días de la Semana:
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                      {['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'].map(dia => (
                        <label key={dia} style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                          <input
                            type="checkbox"
                            checked={cliente.dias_auditoria?.includes(dia) || false}
                            onChange={(e) => {
                              const dias = cliente.dias_auditoria || [];
                              if (e.target.checked) {
                                setCliente(c => ({ ...c, dias_auditoria: [...dias, dia] }));
                              } else {
                                setCliente(c => ({ ...c, dias_auditoria: dias.filter(d => d !== dia) }));
                              }
                            }}
                          />
                          <span style={{ fontSize: '0.875rem' }}>{dia}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </label>
              )}

              {cliente.modalidad_auditoria_cliente === "por fecha exacta" && (
                <label style={formStyles.label}>
                  Fecha Exacta:
                  <input
                    type="date"
                    style={formStyles.input}
                    value={cliente.fecha_exacta || ''}
                    onChange={e => setCliente(c => ({ ...c, fecha_exacta: e.target.value }))}
                  />
                </label>
              )}
              
              <label style={formStyles.label}>
                Instrucciones para Operadores:
                <textarea
                  style={{...formStyles.input, minHeight: '100px', resize: 'vertical'}}
                  value={cliente.instrucciones_operador || ''}
                  onChange={e => setCliente(c => ({ ...c, instrucciones_operador: e.target.value }))}
                  placeholder="Describe el layout, procedimientos específicos, puntos importantes que debe revisar el operador..."
                />
              </label>
              
              <label style={formStyles.label}>
                Archivo de Procedimientos (opcional):
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <input
                    ref={fileInputRef}
                    type="file"
                    style={formStyles.input}
                    accept=".pdf,.doc,.docx,.txt,.jpg,.png"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        setCliente(c => ({
                          ...c,
                          archivo_procedimiento: {
                            nombre: file.name,
                            tamaño: file.size,
                            tipo: file.type,
                            fechaSubida: new Date().toISOString(),
                            file: file
                          }
                        }));
                      }
                    }}
                  />
                  <span style={{ fontSize: '0.75rem', color: '#666' }}>
                    PDF, DOC, TXT, IMG
                  </span>
                </div>
              </label>
            </div>
          </>
        )}

        {/* 8. TILDE 4 AUDITORÍAS FIJAS - SOLO CUANDO NO HAY SUCURSALES */}
        {cliente.sucursales.length === 0 && (
          <div style={{...formStyles.section, backgroundColor: '#f0f8f0', border: '2px solid #4CAF50'}}>
            <h4 style={{ color: "#4CAF50", marginBottom: '1rem', display: 'flex', alignItems: 'center' }}>
              💰 Sistema de 4 Auditorías Fijas
            </h4>
            
            <label style={{ 
              display: "flex", 
              alignItems: "center", 
              marginBottom: '1rem',
              padding: '1rem',
              backgroundColor: 'white',
              borderRadius: '8px',
              border: cliente.auditoria_fija_mes ? '2px solid #4CAF50' : '1px solid #555'
            }}>
              <input
                type="checkbox"
                checked={cliente.auditoria_fija_mes}
                onChange={e => setCliente(c => ({ ...c, auditoria_fija_mes: e.target.checked }))}
                style={{ marginRight: '0.75rem', transform: 'scale(1.3)' }}
              />
              <div>
                <strong style={{ color: cliente.auditoria_fija_mes ? '#4CAF50' : '#333', fontSize: '16px' }}>
                  ✅ Cliente con 4 Auditorías Fijas Mensuales + Extras
                </strong>
                <p style={{ 
                  margin: '0.5rem 0 0 0', 
                  fontSize: '14px', 
                  color: '#666',
                  lineHeight: '1.4'
                }}>
                  Si marcas este tilde: El 1° de cada mes se facturan automáticamente 4 auditorías.
                  Las auditorías adicionales (5ta, 6ta, etc.) se cobran por separado.
                  <br />
                  <span style={{ color: cliente.auditoria_fija_mes ? '#4CAF50' : '#888', fontWeight: '600' }}>
                    {cliente.auditoria_fija_mes 
                      ? `💰 Sistema activado - 4 auditorías fijas mensuales`
                      : '❌ Sistema desactivado - Se cobra por auditoría individual'
                    }
                  </span>
                </p>
              </div>
            </label>
          </div>
        )}



        {/* 3. CONTRATO OBLIGATORIO - SOLO CUANDO NO HAY SUCURSALES */}
        {cliente.sucursales.length === 0 && (
          <div style={{
            ...formStyles.section, 
            backgroundColor: contratoValido ? '#f0f8f0' : '#fdf2f2', 
            border: contratoValido ? '2px solid #4CAF50' : '2px solid #f44336'
          }}>
            <h4 style={{ 
              color: contratoValido ? "#4CAF50" : "#f44336", 
              marginBottom: '1rem', 
              display: 'flex', 
              alignItems: 'center',
              fontSize: '18px',
              fontWeight: '600'
            }}>
              📄 Contrato {contratoValido ? '✅' : '❌ OBLIGATORIO'}
            </h4>
            
            <div style={formStyles.grid}>
              <label style={formStyles.label}>
                Fecha de Firma *:
                <input
                  type="date"
                  style={{
                    ...formStyles.input,
                    borderColor: cliente.contrato_fecha_firma ? '#4CAF50' : '#f44336'
                  }}
                  value={cliente.contrato_fecha_firma}
                  onChange={e => setCliente(c => ({ ...c, contrato_fecha_firma: e.target.value }))}
                  required
                />
              </label>
              
              <label style={formStyles.label}>
                Archivo del Contrato *:
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <input
                    ref={contratoInputRef}
                    type="file"
                    style={{ display: 'none' }}
                    accept=".pdf,.doc,.docx,.jpg,.png"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        setCliente(c => ({
                          ...c,
                          contrato_adjunto: {
                            nombre: file.name,
                            tamaño: file.size,
                            tipo: file.type,
                            fechaSubida: new Date().toISOString(),
                            file: file
                          }
                        }));
                      }
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => contratoInputRef.current?.click()}
                    className="btn btn-primary"
                    style={{
                      backgroundColor: contratoValido ? '#4CAF50' : '#f44336',
                      color: 'white',
                      border: 'none',
                      padding: '0.5rem 1rem',
                      borderRadius: '4px'
                    }}
                  >
                    📎 {contratoValido ? 'Cambiar Contrato' : 'Adjuntar Contrato'}
                  </button>
                  {contratoValido && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                      <span style={{
                        color: '#4CAF50',
                        fontSize: '14px',
                        fontWeight: '500'
                      }}>
                        ✅ {cliente.contrato_adjunto.nombre || cliente.contrato_adjunto.name}
                      </span>
                      {cliente.contrato_adjunto && (
                        <button
                          type="button"
                          onClick={() => {
                            try {
                              const contrato = cliente.contrato_adjunto;

                              // Intentar obtener el archivo de diferentes ubicaciones posibles
                              let fileData = null;

                              // Caso 1: Archivo recién subido (tiene propiedad .file)
                              if (contrato.file) {
                                if (typeof contrato.file === 'string') {
                                  fileData = contrato.file;
                                } else if (contrato.file instanceof File) {
                                  // Convertir File a base64
                                  const reader = new FileReader();
                                  reader.onload = (e) => {
                                    const link = document.createElement('a');
                                    link.href = e.target.result;
                                    link.download = contrato.nombre || contrato.name || 'contrato.pdf';
                                    document.body.appendChild(link);
                                    link.click();
                                    document.body.removeChild(link);
                                  };
                                  reader.readAsDataURL(contrato.file);
                                  return;
                                }
                              }

                              // Caso 2: Archivo guardado en BD (base64 directo en diferentes propiedades)
                              if (!fileData) {
                                fileData = contrato.data || contrato.base64 || contrato.contenido || contrato.archivo;
                              }

                              // Verificar si tenemos datos válidos
                              if (!fileData || (typeof fileData === 'string' && fileData.length < 50)) {
                                alert('❌ Este contrato no tiene archivo disponible para descarga.\n\nPara solucionarlo:\n1. Vuelve a adjuntar el archivo del contrato\n2. Guarda los cambios');
                                return;
                              }

                              // Asegurar que tiene el prefijo data: correcto
                              if (!fileData.startsWith('data:')) {
                                const tipo = contrato.tipo || contrato.type || 'application/pdf';
                                fileData = `data:${tipo};base64,${fileData}`;
                              }

                              const link = document.createElement('a');
                              link.href = fileData;
                              link.download = contrato.nombre || contrato.name || 'contrato.pdf';
                              document.body.appendChild(link);
                              link.click();
                              document.body.removeChild(link);
                            } catch (error) {
                              console.error('Error al descargar:', error);
                              alert('Error al descargar el contrato');
                            }
                          }}
                          style={{
                            backgroundColor: '#2196F3',
                            color: 'white',
                            border: 'none',
                            padding: '0.25rem 0.5rem',
                            borderRadius: '4px',
                            fontSize: '12px',
                            cursor: 'pointer'
                          }}
                        >
                          🔽 Descargar
                        </button>
                      )}
                    </div>
                  )}
                </div>
                {!contratoValido && (
                  <small style={{ color: '#f44336', fontSize: '12px', fontWeight: '500' }}>
                    ⚠️ El contrato es obligatorio para crear/editar el cliente
                  </small>
                )}
              </label>
            </div>
            
            <label style={formStyles.label}>
              Observaciones del Contrato:
              <textarea
                style={{
                  ...formStyles.input,
                  minHeight: '80px',
                  resize: 'vertical'
                }}
                value={cliente.contrato_observaciones}
                onChange={e => setCliente(c => ({ ...c, contrato_observaciones: e.target.value }))}
                placeholder="Observaciones sobre el contrato, condiciones especiales, etc..."
              />
            </label>
          </div>
        )}

        {/* 9. CREAR USUARIOS - SOLO CUANDO NO HAY SUCURSALES */}
        {cliente.sucursales.length === 0 && (
          <div style={formStyles.section}>
            <h4 style={{ color: "#4CAF50", marginBottom: '1rem' }}>
              <label style={{ display: "flex", alignItems: "center" }}>
                <input
                  type="checkbox"
                  checked={cliente.crearUsuario}
                  onChange={e => setCliente(c => ({ ...c, crearUsuario: e.target.checked }))}
                  style={{ marginRight: '0.5rem' }}
                />
                ✨ Crear usuario asociado
              </label>
            </h4>
            {cliente.crearUsuario && (
              <>
                <div style={formStyles.grid}>
                  <label style={formStyles.label}>
                    Username *:
                    <input
                      type="text"
                      style={formStyles.input}
                      value={cliente.username}
                      onChange={e => setCliente(c => ({ ...c, username: e.target.value }))}
                      required
                      placeholder="Usuario para login"
                    />
                  </label>
                  <label style={formStyles.label}>
                    Rol *:
                    <select
                      style={formStyles.input}
                      value={cliente.rol}
                      onChange={e => setCliente(c => ({ ...c, rol: e.target.value }))}
                      required
                    >
                      {ROLES_USUARIO.map(r => (
                        <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>
                      ))}
                    </select>
                  </label>
                </div>
                <label style={formStyles.label}>
                  Password *:
                  <div style={{ display: "flex", gap: '0.5rem' }}>
                    <input
                      type={cliente.mostrarPassword ? "text" : "password"}
                      style={{ ...formStyles.input, flex: 1 }}
                      value={cliente.password}
                      onChange={e => setCliente(c => ({ ...c, password: e.target.value }))}
                      placeholder="Mínimo 6 caracteres"
                      required
                    />
                    <button 
                      type="button" 
                      onClick={generarPassword} 
                      className="btn btn-primary"
                      style={{ padding: "0.75rem", fontSize: "0.875rem" }}
                    >
                      🎲
                    </button>
                    <button 
                      type="button" 
                      onClick={() => setCliente(c => ({ ...c, mostrarPassword: !c.mostrarPassword }))} 
                      className="btn btn-secondary"
                      style={{ padding: "0.75rem", fontSize: "0.875rem" }}
                    >
                      {cliente.mostrarPassword ? "🙈" : "👁️"}
                    </button>
                  </div>
                </label>
                <label style={formStyles.label}>
                  Confirmar Password *:
                  <input
                    type={cliente.mostrarPassword ? "text" : "password"}
                    style={{
                      ...formStyles.input,
                      borderColor: !passwordsCoinciden && cliente.confirmarPassword ? "#f44336" : "#555"
                    }}
                    value={cliente.confirmarPassword}
                    onChange={e => setCliente(c => ({ ...c, confirmarPassword: e.target.value }))}
                    required
                  />
                  {!passwordsCoinciden && cliente.confirmarPassword && (
                    <small style={{ color: "#f44336", display: "block", marginTop: '0.25rem' }}>
                      ⚠️ Las contraseñas no coinciden
                    </small>
                  )}
                  {passwordsCoinciden && cliente.password && cliente.confirmarPassword && (
                    <small style={{ color: "#4CAF50", display: "block", marginTop: '0.25rem' }}>
                      ✅ Las contraseñas coinciden
                    </small>
                  )}
                </label>
              </>
            )}
          </div>
        )}

        {/* Sucursales */}
        <div style={formStyles.section}>
          <h4 style={{ color: "#4CAF50", marginBottom: '1rem' }}>🏢 Sucursales del Cliente</h4>
          
          {cliente.sucursales.length === 0 ? (
            <p style={{ color: "#888", fontStyle: "italic", marginBottom: '1rem' }}>
              No hay sucursales registradas
            </p>
          ) : (
            <div style={{ marginBottom: '1rem', overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #3a3a3a' }}>
                    <th style={{ padding: '0.5rem', textAlign: 'left', color: '#4CAF50' }}>Nombre</th>
                    <th style={{ padding: '0.5rem', textAlign: 'center', color: '#4CAF50' }}>Tiempo</th>
                    <th style={{ padding: '0.5rem', textAlign: 'center', color: '#4CAF50' }}>Precios Cliente</th>
                    <th style={{ padding: '0.5rem', textAlign: 'center', color: '#4CAF50' }}>Tiempo Específico</th>
                    <th style={{ padding: '0.5rem', textAlign: 'center', color: '#4CAF50' }}>Pago Operador</th>
                    <th style={{ padding: '0.5rem', textAlign: 'center', color: '#4CAF50' }}>Sistema Fijas</th>
                    <th style={{ padding: '0.5rem', textAlign: 'left', color: '#4CAF50' }}>Modalidad</th>
                    <th style={{ padding: '0.5rem', textAlign: 'center', color: '#4CAF50' }}>Estado</th>
                    <th style={{ padding: '0.5rem', textAlign: 'center', color: '#4CAF50' }}>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {cliente.sucursales.map((suc, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #3a3a3a' }}>
                      <td style={{ padding: '0.5rem' }}>
                        <div style={{ fontWeight: '500' }}>{suc.nombre}</div>
                        <div style={{ fontSize: '0.75rem', color: '#888' }}>{suc.direccion}</div>
                        {suc.dvr_id && (
                          <div style={{ fontSize: '0.65rem', color: '#666' }}>🖥️ DVR: {suc.dvr_id}</div>
                        )}
                      </td>
                      <td style={{ padding: '0.5rem', textAlign: 'center' }}>
                        <div style={{ fontSize: '0.8rem', fontWeight: '500', color: '#4CAF50' }}>
                          ⏱️ {suc.tiempo_auditoria || 0} min
                        </div>
                        <div style={{ fontSize: '0.7rem', color: '#888' }}>
                          ({Math.round((suc.tiempo_auditoria || 0) / 60 * 10) / 10}h)
                        </div>
                      </td>
                      <td style={{ padding: '0.5rem', textAlign: 'center' }}>
                        <div style={{ fontSize: '0.75rem' }}>
                          <div style={{ color: '#4CAF50' }}>Normal: ${suc.precio_normal}</div>
                          <div style={{ color: '#FF9800' }}>Urgente: ${suc.precio_urgente}</div>
                          <div style={{ color: '#2196F3' }}>Prioritaria: ${suc.precio_prioritaria}</div>
                        </div>
                      </td>
                      <td style={{ padding: '0.5rem', textAlign: 'center' }}>
                        <div style={{ fontSize: '0.75rem', color: '#666' }}>
                          {suc.tiempoEstimadoHoras ? (
                            <div>
                              <div style={{ fontWeight: '500', color: '#2196f3' }}>⚙️ Específico</div>
                              <div>{suc.tiempoEstimadoHoras}h</div>
                            </div>
                          ) : (
                            <div>
                              <div style={{ fontWeight: '500' }}>📋 Del cliente</div>
                              <div>{cliente.tiempoEstimado}h</div>
                            </div>
                          )}
                        </div>
                      </td>
                      <td style={{ padding: '0.5rem', textAlign: 'center' }}>
                        <div style={{ fontSize: '0.75rem', color: '#666' }}>
                          {suc.tipoComision || suc.comisionOperador || suc.montoFijoOperador ? (
                            <div>
                              <div style={{ fontWeight: '500', color: '#2196f3' }}>⚙️ Específico</div>
                              <div>
                                {(suc.tipoComision || cliente.tipoComision) === 'fijo' 
                                  ? `$${suc.montoFijoOperador || cliente.montoFijoOperador}`
                                  : `${suc.comisionOperador || cliente.comisionOperador}%`
                                }
                              </div>
                            </div>
                          ) : (
                            <div>
                              <div style={{ fontWeight: '500' }}>📋 Del cliente</div>
                              <div>
                                {cliente.tipoComision === 'fijo' 
                                  ? `$${cliente.montoFijoOperador}`
                                  : `${cliente.comisionOperador}%`
                                }
                              </div>
                            </div>
                          )}
                        </div>
                      </td>
                      <td style={{ padding: '0.5rem', textAlign: 'center' }}>
                        <div style={{ 
                          fontSize: '0.75rem', 
                          fontWeight: '600',
                          color: cliente.auditoria_fija_mes || suc.auditoria_fija_mes ? '#4CAF50' : '#f44336'
                        }}>
                          {cliente.auditoria_fija_mes 
                            ? '💰 Cliente (4 fijas)'
                            : suc.auditoria_fija_mes 
                            ? '💰 Sucursal (4 fijas)'
                            : '📊 Individual'
                          }
                        </div>
                        {(cliente.auditoria_fija_mes || suc.auditoria_fija_mes) && (
                          <div style={{ fontSize: '0.65rem', color: '#888' }}>
                            4 fijas + extras
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '0.5rem' }}>
                        <div style={{ fontSize: '0.75rem' }}>
                          {suc.modalidad_auditoria === "por días"
                            ? (suc.dias_auditoria || []).join(", ")
                            : suc.modalidad_auditoria}
                        </div>
                        {suc.instrucciones_op && (
                          <div style={{ fontSize: '0.65rem', color: '#888' }}>📋 Con instrucciones</div>
                        )}
                      </td>
                      <td style={{ padding: '0.5rem', textAlign: 'center' }}>
                        <span style={{
                          padding: '0.25rem 0.5rem',
                          borderRadius: '0.25rem',
                          fontSize: '0.75rem',
                          backgroundColor: suc.activa ? '#4caf504d' : '#f443364d',
                          color: suc.activa ? '#4CAF50' : '#f44336'
                        }}>
                          {suc.activa ? 'Activa' : 'Inactiva'}
                        </span>
                      </td>
                      <td style={{ padding: '0.5rem', textAlign: 'center' }}>
                        <button
                          type="button"
                          onClick={() => {
                            setEditingSuc(i);
                            setSucursal({ ...suc });
                          }}
                          style={{
                            padding: '0.25rem 0.5rem',
                            backgroundColor: '#2196F3',
                            color: 'white',
                            border: 'none',
                            borderRadius: '0.25rem',
                            fontSize: '0.75rem',
                            cursor: 'pointer',
                            marginRight: '0.25rem'
                          }}
                        >
                          Editar
                        </button>
                        <button
                          type="button"
                          onClick={() => eliminarSucursal(i)}
                          style={{
                            padding: '0.25rem 0.5rem',
                            backgroundColor: '#f44336',
                            color: 'white',
                            border: 'none',
                            borderRadius: '0.25rem',
                            fontSize: '0.75rem',
                            cursor: 'pointer'
                          }}
                        >
                          Eliminar
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <button
            type="button"
            onClick={() => {
              setEditingSuc(null);
              setSucursal({
                nombre: "", 
                direccion: "",
                dias_auditoria: [], 
                modalidad_auditoria: "manual/hoy",
                urgencia: false, 
                prioritaria: false, 
                activa: true,
                instrucciones_op: "",
                dvr_id: "",
                dvr_password: "",
                // Precios (cálculo automático correcto)
                precio_normal: cliente.costoAuditoria,
                precio_prioritaria: cliente.costoAuditoria * 1.5, // 50% más
                precio_urgente: cliente.costoAuditoria * 2, // El doble
                // Tiempo por defecto
                tiempo_auditoria: 120, // 2 horas por defecto
                // Tiempo específico heredado del cliente (convertir de HH:MM a horas decimales)
                tiempoEstimadoHoras: parseFloat(cliente.tiempoEstimado.split(':')[0]) + parseFloat(cliente.tiempoEstimado.split(':')[1])/60,
                // Sistema 4 auditorías fijas (PUEDE ser diferente por sucursal)
                auditoria_fija_mes: cliente.auditoria_fija_mes, // Hereda del cliente por defecto
                // Pago operador - heredar valores del cliente por defecto (opcionales)
                comisionOperador: cliente.comisionOperador,
                montoFijoOperador: cliente.montoFijoOperador,
                tipoComision: cliente.tipoComision
              });
            }}
            className="btn btn-primary"
          >
            ➕ Agregar Sucursal
          </button>

          {sucursal && (
            <div style={{ marginTop: '1rem', padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #e0e0e0' }}>
              <h5 style={{ color: "#4CAF50", marginBottom: '1rem' }}>
                {editingSuc !== null ? "✏️ Editar Sucursal" : "➕ Nueva Sucursal"}
              </h5>
              
              {/* Datos básicos específicos de sucursal */}
              <div style={formStyles.grid}>
                <label style={formStyles.label}>
                  Nombre de Sucursal *:
                  <input
                    style={formStyles.input}
                    value={sucursal.nombre || ""}
                    onChange={e => setSucursal(s => s ? {...s, nombre: e.target.value} : null)}
                    placeholder="Nombre de la sucursal"
                    required
                  />
                </label>
                <label style={formStyles.label}>
                  Dirección de la Sucursal:
                  <input
                    style={formStyles.input}
                    value={sucursal.direccion || ""}
                    onChange={e => setSucursal(s => s ? {...s, direccion: e.target.value} : null)}
                    placeholder="Dirección específica de esta sucursal"
                  />
                </label>
              </div>

              {/* Configuración DVR */}
              <div style={formStyles.grid}>
                <label style={formStyles.label}>
                  DVR ID/IP:
                  <input
                    style={formStyles.input}
                    value={sucursal.dvr_id || ""}
                    onChange={e => setSucursal(s => s ? {...s, dvr_id: e.target.value} : null)}
                    placeholder="192.168.1.100 o ID del DVR"
                  />
                </label>
                <label style={formStyles.label}>
                  DVR Password:
                  <input
                    type="text"
                    style={formStyles.input}
                    value={sucursal.dvr_password || ""}
                    onChange={e => setSucursal(s => s ? {...s, dvr_password: e.target.value} : null)}
                    placeholder="Contraseña del DVR (opcional)"
                  />
                </label>
              </div>

              <div style={formStyles.grid3}>
                <label style={formStyles.label}>
                  Precio Normal ($):
                  <input
                    type="number"
                    step="0.01"
                    style={formStyles.input}
                    value={sucursal.precio_normal || 0}
                    onChange={e => {
                      const precioNormal = parseFloat(e.target.value) || 0;
                      setSucursal(s => s ? {
                        ...s, 
                        precio_normal: precioNormal,
                        precio_prioritaria: precioNormal * 1.5, // Automático: 50% más
                        precio_urgente: precioNormal * 2, // Automático: el doble
                        // También actualizar pagos operador
                        pago_operador_normal: Math.round(precioNormal * 0.8),
                        pago_operador_prioritaria: Math.round((precioNormal * 1.5) * 0.8),
                        pago_operador_urgente: Math.round((precioNormal * 2) * 0.8)
                      } : null);
                    }}
                  />
                </label>
                <label style={formStyles.label}>
                  Precio Prioritaria ($ - Automático):
                  <input
                    type="number"
                    step="0.01"
                    style={{...formStyles.input, backgroundColor: '#f0f0f0', color: '#666'}}
                    value={sucursal.precio_prioritaria || 0}
                    readOnly
                  />
                  <small style={{ color: '#666', fontSize: '0.75rem' }}>
                    Auto-calculado: Normal + 50%
                  </small>
                </label>
                <label style={formStyles.label}>
                  Precio Urgente ($ - Automático):
                  <input
                    type="number"
                    step="0.01"
                    style={{...formStyles.input, backgroundColor: '#f0f0f0', color: '#666'}}
                    value={sucursal.precio_urgente || 0}
                    readOnly
                  />
                  <small style={{ color: '#666', fontSize: '0.75rem' }}>
                    Auto-calculado: Normal × 2
                  </small>
                </label>
              </div>

              {/* Configuración específica de tiempo y pago para esta sucursal */}
              <div style={{ marginTop: '1.5rem', padding: '1rem', backgroundColor: '#f0f8f0', borderRadius: '6px', border: '1px solid #4CAF50' }}>
                <h6 style={{ color: '#4CAF50', marginBottom: '1rem', fontSize: '14px', fontWeight: '600' }}>
                  ⚙️ Configuración Específica de esta Sucursal
                </h6>
                
                <div style={{backgroundColor: '#e3f2fd', padding: '1rem', borderRadius: '8px', border: '1px solid #2196f3', marginBottom: '1rem'}}>
                  <p style={{ color: "#1976d2", marginBottom: '0.5rem', fontSize: '13px' }}>
                    💡 <strong>Esta sucursal puede tener configuración específica diferente al cliente.</strong><br/>
                    Si dejas los campos vacíos, heredará la configuración del cliente principal.
                  </p>
                  <div style={{ fontSize: '12px', color: '#555', marginTop: '0.5rem' }}>
                    <strong>Valores del cliente:</strong> Tiempo: {cliente.tiempoEstimado}h | 
                    Pago: {cliente.tipoComision === 'fijo' ? `$${cliente.montoFijoOperador} fijo` : `${cliente.comisionOperador}%`}
                  </div>
                </div>

                <div style={formStyles.grid}>
                  <label style={formStyles.label}>
                    Tiempo Específico (horas):
                    <input
                      type="number"
                      step="0.25"
                      min="0"
                      style={formStyles.input}
                      value={sucursal.tiempoEstimadoHoras || ''}
                      onChange={e => setSucursal(s => s ? {...s, tiempoEstimadoHoras: parseFloat(e.target.value) || undefined} : null)}
                      placeholder={`Heredar del cliente (${cliente.tiempoEstimado}h)`}
                    />
                    <small style={{color: '#666', fontSize: '0.75rem'}}>Dejar vacío para heredar del cliente</small>
                  </label>

                  <label style={formStyles.label}>
                    Tipo de Comisión:
                    <select
                      style={formStyles.input}
                      value={sucursal.tipoComision || ''}
                      onChange={e => setSucursal(s => s ? {...s, tipoComision: e.target.value || undefined} : null)}
                    >
                      <option value="">Heredar del cliente ({cliente.tipoComision})</option>
                      <option value="porcentaje">Porcentaje (%)</option>
                      <option value="fijo">Monto Fijo ($)</option>
                    </select>
                  </label>
                </div>

                <div style={formStyles.grid}>
                  {(sucursal.tipoComision === 'porcentaje' || (!sucursal.tipoComision && cliente.tipoComision === 'porcentaje')) && (
                    <label style={formStyles.label}>
                      Comisión Operador (%):
                      <input
                        type="number"
                        min="0"
                        max="100"
                        style={formStyles.input}
                        value={sucursal.comisionOperador || ''}
                        onChange={e => setSucursal(s => s ? {...s, comisionOperador: parseFloat(e.target.value) || undefined} : null)}
                        placeholder={`Heredar del cliente (${cliente.comisionOperador}%)`}
                      />
                      <small style={{color: '#666', fontSize: '0.75rem'}}>Dejar vacío para heredar del cliente</small>
                    </label>
                  )}

                  {(sucursal.tipoComision === 'fijo' || (!sucursal.tipoComision && cliente.tipoComision === 'fijo')) && (
                    <label style={formStyles.label}>
                      Monto Fijo Operador ($):
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        style={formStyles.input}
                        value={sucursal.montoFijoOperador || ''}
                        onChange={e => setSucursal(s => s ? {...s, montoFijoOperador: parseFloat(e.target.value) || undefined} : null)}
                        placeholder={`Heredar del cliente ($${cliente.montoFijoOperador})`}
                      />
                      <small style={{color: '#666', fontSize: '0.75rem'}}>Dejar vacío para heredar del cliente</small>
                    </label>
                  )}
                </div>

                {/* Mostrar ejemplo de cálculo */}
                {sucursal.tiempoEstimadoHoras || sucursal.comisionOperador || sucursal.montoFijoOperador ? (
                  <div style={{ marginTop: '1rem', padding: '0.75rem', backgroundColor: '#e8f5e8', borderRadius: '6px', border: '1px solid #4CAF50' }}>
                    <strong style={{color: '#2e7d32'}}>Vista previa para esta sucursal:</strong>
                    <div style={{ fontSize: '13px', color: '#555', marginTop: '0.25rem' }}>
                      Tiempo: {sucursal.tiempoEstimadoHoras || parseFloat(cliente.tiempoEstimado.split(':')[0]) + parseFloat(cliente.tiempoEstimado.split(':')[1])/60}h | 
                      Pago: {(sucursal.tipoComision || cliente.tipoComision) === 'fijo' 
                        ? `$${sucursal.montoFijoOperador || cliente.montoFijoOperador} fijo`
                        : `${sucursal.comisionOperador || cliente.comisionOperador}% = $${((cliente.costoAuditoria * (sucursal.comisionOperador || cliente.comisionOperador)) / 100).toFixed(2)}`
                      }
                    </div>
                  </div>
                ) : null}
              </div>

              <label style={formStyles.label}>
                Modalidad de Auditoría:
                <select
                  style={formStyles.input}
                  value={sucursal.modalidad_auditoria}
                  onChange={e => setSucursal(s => s ? {
                    ...s,
                    modalidad_auditoria: e.target.value,
                    dias_auditoria: []
                  } : null)}
                >
                  <option value="manual/hoy">Manual/Hoy</option>
                  <option value="por días">Por días de la semana</option>
                  <option value="por fecha exacta">Por fecha exacta</option>
                </select>
              </label>

              {sucursal.modalidad_auditoria === "por días" && (
                <div style={{ marginTop: '1rem' }}>
                  <label style={formStyles.label}>Días de la semana:</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.5rem' }}>
                    {DIAS.map(dia => (
                      <label key={dia} style={{ display: 'flex', alignItems: 'center', color: '#e0e0e0' }}>
                        <input
                          type="checkbox"
                          checked={(sucursal.dias_auditoria || []).includes(dia)}
                          onChange={e => {
                            const arr = sucursal.dias_auditoria || [];
                            setSucursal(s => s ? {
                              ...s,
                              dias_auditoria: e.target.checked
                                ? [...arr, dia]
                                : arr.filter(d => d !== dia)
                            } : null);
                          }}
                          style={{ marginRight: '0.25rem' }}
                        />
                        {dia}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {sucursal.modalidad_auditoria === "por fecha exacta" && (
                <label style={formStyles.label}>
                  Fecha Exacta:
                  <input
                    type="date"
                    style={formStyles.input}
                    value={sucursal.fecha_exacta || ""}
                    onChange={e => setSucursal(s => s ? {...s, fecha_exacta: e.target.value} : null)}
                  />
                </label>
              )}

              {/* Instrucciones para Operadores */}
              <label style={formStyles.label}>
                Instrucciones para Operadores:
                <textarea
                  style={{
                    ...formStyles.input,
                    minHeight: '100px',
                    resize: 'vertical'
                  }}
                  value={sucursal.instrucciones_op || ""}
                  onChange={e => setSucursal(s => s ? {...s, instrucciones_op: e.target.value} : null)}
                  placeholder="Describe el layout, procedimientos específicos, puntos importantes que debe revisar el operador en esta sucursal..."
                />
              </label>

              {/* Archivo de Procedimientos */}
              <div style={{ marginTop: '1rem' }}>
                <label style={formStyles.label}>
                  Archivo de Procedimientos:
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <input
                      type="file"
                      style={formStyles.input}
                      accept=".pdf,.doc,.docx,.txt,.jpg,.png"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          setSucursal(s => s ? {
                            ...s, 
                            archivo_procedimiento: {
                              nombre: file.name,
                              tamaño: file.size,
                              tipo: file.type,
                              fechaSubida: new Date().toISOString()
                            }
                          } : null);
                        }
                      }}
                    />
                    <span style={{ fontSize: '0.75rem', color: '#888' }}>
                      PDF, DOC, TXT, IMG
                    </span>
                  </div>
                </label>
                {sucursal.archivo_procedimiento && (
                  <div style={{ 
                    marginTop: '0.5rem', 
                    padding: '0.5rem', 
                    backgroundColor: '#4a4a4a', 
                    borderRadius: '4px',
                    fontSize: '0.875rem'
                  }}>
                    📎 {sucursal.archivo_procedimiento.nombre} 
                    <span style={{ color: '#888', marginLeft: '0.5rem' }}>
                      ({(sucursal.archivo_procedimiento.tamaño / 1024).toFixed(1)} KB)
                    </span>
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginTop: '1rem' }}>
                <label style={{ display: 'flex', alignItems: 'center', color: '#e0e0e0' }}>
                  <input
                    type="checkbox"
                    checked={!!sucursal.activa}
                    onChange={e => setSucursal(s => s ? {...s, activa: e.target.checked} : null)}
                    style={{ marginRight: '0.5rem' }}
                  />
                  Sucursal Activa
                </label>
                
                {/* Sistema 4 auditorías fijas POR SUCURSAL individual */}
                <label style={{ 
                  display: 'flex', 
                  alignItems: 'flex-start', 
                  color: '#e0e0e0',
                  padding: '0.75rem',
                  backgroundColor: sucursal.auditoria_fija_mes ? '#1a4a1a' : '#2a2a2a',
                  borderRadius: '6px',
                  border: sucursal.auditoria_fija_mes ? '2px solid #4CAF50' : '1px solid #555'
                }}>
                  <input
                    type="checkbox"
                    checked={!!sucursal.auditoria_fija_mes}
                    disabled={cliente.auditoria_fija_mes} // Deshabilitar si el cliente tiene tilde general
                    onChange={e => setSucursal(s => s ? {...s, auditoria_fija_mes: e.target.checked} : null)}
                    style={{ 
                      marginRight: '0.5rem', 
                      marginTop: '0.1rem',
                      opacity: cliente.auditoria_fija_mes ? 0.5 : 1
                    }}
                  />
                  <div>
                    <strong style={{ color: sucursal.auditoria_fija_mes ? '#4CAF50' : '#e0e0e0' }}>
                      💰 4 Auditorías Fijas para ESTA sucursal
                    </strong>
                    <p style={{ 
                      margin: '0.25rem 0 0 0', 
                      fontSize: '12px', 
                      color: '#aaa',
                      lineHeight: '1.3'
                    }}>
                      {cliente.auditoria_fija_mes
                        ? '⚠️ El cliente ya tiene 4 fijas generales - No se puede configurar por sucursal'
                        : sucursal.auditoria_fija_mes 
                        ? '✅ Esta sucursal factura 4 fijas + extras independientemente del cliente'
                        : '❌ Se cobra por auditoría individual en esta sucursal'
                      }
                    </p>
                  </div>
                </label>
              </div>

              <div style={{ marginTop: '1rem' }}>
                <button 
                  type="button"
                  onClick={handleSucGuardar} 
                  className="btn btn-primary"
                  style={{ marginRight: '0.5rem' }}
                >
                  ✅ Guardar Sucursal
                </button>
                <button 
                  type="button"
                  onClick={() => { setSucursal(null); setEditingSuc(null); }} 
                  className="btn btn-secondary"
                >
                  ❌ Cancelar
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Botones de acción */}
        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', marginTop: '2rem' }}>
          <button 
            type="button" 
            onClick={onCancel}
            className="btn btn-secondary"
            disabled={loading}
          >
            Cancelar
          </button>
          <button 
            type="submit"
            className="btn btn-primary"
            disabled={loading || (cliente.crearUsuario && !passwordsCoinciden)}
          >
            {loading ? (isEditing ? 'Guardando...' : 'Creando...') : (isEditing ? 'Guardar Cambios' : 'Crear Cliente Completo')}
          </button>
        </div>
      </form>

      <input 
        ref={fileInputRef}
        type="file" 
        style={{ display: 'none' }} 
        accept=".pdf,.doc,.docx,.txt" 
      />

      {/* Lista de Clientes Existentes */}
      <div style={{ marginTop: '3rem', padding: '2rem', backgroundColor: '#2a2a2a', borderRadius: '10px' }}>
        <h3 style={{ color: '#4CAF50', marginBottom: '1.5rem', fontSize: '1.5rem' }}>
          📋 Clientes Registrados ({clientesExistentes.length})
        </h3>
        
        {clientesExistentes.length === 0 ? (
          <p style={{ color: '#888', fontStyle: 'italic' }}>
            No hay clientes registrados aún.
          </p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #4CAF50' }}>
                  <th style={{ padding: '0.75rem', textAlign: 'left', color: '#4CAF50' }}>Nombre</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', color: '#4CAF50' }}>Email</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', color: '#4CAF50' }}>Teléfono</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', color: '#4CAF50' }}>Contacto</th>
                  <th style={{ padding: '0.75rem', textAlign: 'center', color: '#4CAF50' }}>Estado</th>
                  <th style={{ padding: '0.75rem', textAlign: 'center', color: '#4CAF50' }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {clientesExistentes.map((client, index) => (
                  <tr key={client.id || index} style={{ borderBottom: '1px solid #3a3a3a' }}>
                    <td style={{ padding: '0.75rem', color: '#fff' }}>
                      <div style={{ fontWeight: '500' }}>{client.nombre}</div>
                      {client.rut && (
                        <div style={{ fontSize: '0.75rem', color: '#888' }}>RUT: {client.rut}</div>
                      )}
                    </td>
                    <td style={{ padding: '0.75rem', color: '#fff' }}>{client.email}</td>
                    <td style={{ padding: '0.75rem', color: '#fff' }}>{client.telefono}</td>
                    <td style={{ padding: '0.75rem', color: '#fff' }}>{client.contactoPrincipal}</td>
                    <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                      <span style={{
                        padding: '0.25rem 0.5rem',
                        borderRadius: '0.25rem',
                        fontSize: '0.75rem',
                        backgroundColor: client.activo ? '#4CAF5020' : '#ff980020',
                        color: client.activo ? '#4CAF50' : '#ff9800',
                        border: `1px solid ${client.activo ? '#4CAF50' : '#ff9800'}`
                      }}>
                        {client.activo ? '✅ Activo' : '⏸️ Inactivo'}
                      </span>
                    </td>
                    <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                      <button
                        type="button"
                        onClick={() => handleEditClient(client)}
                        style={{
                          padding: '0.375rem 0.75rem',
                          backgroundColor: '#4CAF50',
                          color: 'white',
                          border: 'none',
                          borderRadius: '0.25rem',
                          fontSize: '0.75rem',
                          cursor: 'pointer',
                          marginRight: '0.5rem'
                        }}
                      >
                        ✏️ Editar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
    </>
  );
};

export default ClientFormComplete;